import XLSX from 'xlsx-js-style'
import { Download16 } from '../../../images/icons/icons_theme';

/** 
 * @충실도전용엑셀다운로드
    <FidelityExcelDownload 
        targetRef = {tableRef} // 엑셀로 추출할 테이블 ref
        disabled = {!(isSendSearched && sendFidelityData.length)} // disabled 정의
        excelFileName = {excelFileName}
        colWidths={[7, 17, 17, 30, 30, 30, 30, 30, 30]}
    />
 * **/

const FidelityExcelDownload = ({ targetRef, disabled = false, excelFileName, colWidths }) => {

    const excelDownload = () => {
        if (!targetRef || !targetRef.current) {
            console.warn('엑셀로 추출할 테이블을 찾을 수 없습니다.');
            return;
        }

        // 화면에는 영향을 주지 않는 임시 DOM 복제본 생성
        const cloneTable = targetRef.current.cloneNode(true);

        // 화면에 적용된 정렬을 엑셀에 그대로 반영하기 위해, 원본 셀의 계산된 text-align을 복제본에 data 속성으로 옮기기
        const originCells = targetRef.current.querySelectorAll('td, th');
        const clonedCells = cloneTable.querySelectorAll('td, th');
        originCells.forEach((origin, idx) => {
            const cloned = clonedCells[idx];
            if (!cloned) return;

            const align = window.getComputedStyle(origin).textAlign;
            cloned.dataset.excelAlign = align === 'start' ? 'left' : align === 'end' ? 'right' : align;
        });

        // 복제된 테이블 내의 범례 셀들을 엑셀 전용 분리 셀(td)로 변환
        // 분리 셀이 항상 시트 전체 폭을 채우도록 총 컬럼 수(colWidths 기준)로 span 계산 (colWidths 미지정 시 기존과 동일한 9칸 기준)
        const totalCols = colWidths && colWidths.length > 0 ? colWidths.length : 9;
        const allTds = cloneTable.querySelectorAll('td, th');
        allTds.forEach((td) => {
            const text = td.textContent || '';
            
            // CASE A: '■ 범례 * 참고' 가 모두 있는 경우 -> 3개 셀로 분할
            if (text.includes('■') && text.includes('센터급, 기관급 공통항목') && text.includes('*')) {
                const parentRow = td.parentElement;
                if (parentRow) {
                    const td1 = document.createElement('td');
                    td1.textContent = '■';
                    td1.className = 'excel_blue_text';

                    const td2 = document.createElement('td');
                    td2.textContent = '센터급, 기관급 공통항목';
                    td2.className = 'excel_black_text';
                    td2.colSpan = 2;

                    const td3 = document.createElement('td');
                    td3.textContent = '* 재검토 항목';
                    td3.className = 'excel_red_text';
                    td3.colSpan = totalCols - 3;

                    parentRow.insertBefore(td1, td);
                    parentRow.insertBefore(td2, td);
                    parentRow.insertBefore(td3, td);
                    parentRow.removeChild(td); // 기존 1개짜리 td 제거
                }
            } 
            // CASE B: '■ 범례' 만 있는 경우 -> 2개 셀로 분할
            if (text.includes('■') && text.includes('센터급, 기관급 공통항목')) {
                const parentRow = td.parentElement;
                if (parentRow) {
                    const td1 = document.createElement('td');
                    td1.textContent = '■';
                    td1.className = 'excel_blue_text';

                    const td2 = document.createElement('td');
                    td2.textContent = '센터급, 기관급 공통항목';
                    td2.className = 'excel_black_text';
                    td2.colSpan = totalCols - 1;

                    parentRow.insertBefore(td1, td);
                    parentRow.insertBefore(td2, td);
                    parentRow.removeChild(td);
                }
            }
        });

        // 변형된 임시 DOM으로 workbook 생성
        const workbook = XLSX.utils.table_to_book(cloneTable, { raw: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const range = XLSX.utils.decode_range(worksheet['!ref']);

        // 공통 테두리 및 스타일 정의
        const commonBorder = {
            top: { style: 'thin', color: { rgb: 'CCCCCC' } },
            bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
            left: { style: 'thin', color: { rgb: 'CCCCCC' } },
            right: { style: 'thin', color: { rgb: 'CCCCCC' } },
        };

        const defaultBodyStyle = {
            font: { name: '맑은 고딕', sz: 10 },
            alignment: { horizontal: 'center', vertical: 'center' },
        };

        const titleStyle = {
            fill: { fgColor: { rgb: '2B579A' } },
            font: { name: '맑은 고딕', sz: 11, bold: true, color: { rgb: 'FFFFFF' } },
            alignment: { horizontal: 'left', vertical: 'center' }
        };

        const headerStyle = {
            fill: { fgColor: { rgb: 'EBEBEB' } },
            font: { name: '맑은 고딕', sz: 10, bold: true },
        };

        const centerStyle = {
            fill: { fgColor: { rgb: 'D4E9FF' } },
            font: { name: '맑은 고딕', sz: 10 },
        };

        const timeStyle = {
            fill: { fgColor: { rgb: '2B579A' } },
            font: { name: '맑은 고딕', sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
            alignment: { horizontal: 'right', vertical: 'center' }
        };

        if (colWidths && colWidths.length > 0) {
            worksheet['!cols'] = colWidths.map((w) => ({ wch: w }));
        }

        // 줄 수 실측 : 실제 폰트로 픽셀 폭을 측정 (엑셀 열 폭 1(wch)은 기본 폰트 기준 약 7px)
        const PX_PER_WCH = 7;
        const measureCtx = document.createElement('canvas').getContext('2d');
        measureCtx.font = '13.4px "Malgun Gothic", "맑은 고딕", sans-serif'; // 본문 10pt 기준

        const countWrappedLines = (line, widthWch) => {
            // 셀 좌우 안쪽 여백을 빼고, 엑셀 렌더링과의 오차를 감안해 5% 좁게 계산
            const availPx = Math.max(PX_PER_WCH, (widthWch * PX_PER_WCH - 10) * 0.95);
            if (measureCtx.measureText(line).width <= availPx) return 1;

            // 폭을 넘는 지점마다 개행 (한글은 글자 단위로 꺾이므로 글자 단위로 누적)
            let lines = 1;
            let cur = 0;
            for (const ch of line) {
                const w = measureCtx.measureText(ch).width;
                if (cur + w > availPx && cur > 0) {
                    lines++;
                    cur = w;
                } else {
                    cur += w;
                }
            }
            return lines;
        };

        // 복제된 테이블의 행 목록 읽기
        const domRows = Array.from(cloneTable.querySelectorAll('tr'));
        const rowHeights = [];

        // (행, 열) -> DOM 셀 매핑표 생성. colspan, rowspan 모두 반영 (xlsx 가 워크시트를 만들 때 쓰는 병합 계산과 동일한 방식)
        const cellMatrix = [];
        domRows.forEach((domRow, rowIdx) => {
            if (!cellMatrix[rowIdx]) cellMatrix[rowIdx] = [];

            let colIdx = 0;
            Array.from(domRow.children).forEach((child) => {
                // 위쪽 행의 rowspan 이 이미 차지한 열은 건너뛰기
                while (cellMatrix[rowIdx][colIdx] !== undefined) colIdx++;

                const colSpan = parseInt(child.getAttribute('colspan') || '1', 10);
                const rowSpan = parseInt(child.getAttribute('rowspan') || '1', 10);

                for (let r = 0; r < rowSpan; r++) {
                    if (!cellMatrix[rowIdx + r]) cellMatrix[rowIdx + r] = [];
                    for (let c = 0; c < colSpan; c++) {
                        cellMatrix[rowIdx + r][colIdx + c] = child;
                    }
                }

                colIdx += colSpan;
            });
        });

        for (let R = range.s.r; R <= range.e.r; ++R) {
            let maxLinesInRow = 1;
            const domRow = domRows[R];
            const domCellMap = cellMatrix[R] || [];

            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                if (!worksheet[cellAddress]) {
                    worksheet[cellAddress] = { t: 's', v: '' };
                }

                const cell = worksheet[cellAddress];
                const domCell = domCellMap[C];

                // 1., 2) 줄바꿈 정리
                if (cell.v && typeof cell.v === 'string') {
                    let str = cell.v;
                    str = str.replace(/<br\s*\/?>\s*(?=\d+[\.\)])/gi, '\n');
                    str = str.replace(/<br\s*\/?>/gi, ' ');
                    str = str.replace(/\r?\n|\r/g, (match, offset, fullStr) => {
                        const rest = fullStr.slice(offset + match.length).trim();
                        return /^\d+[\.\)]/.test(rest) ? '\n' : ' ';
                    });
                    cell.v = str.trim();
                    cell.w = cell.v;
                }

                // 행 높이 계산
                if (cell.v && typeof cell.v === 'string' && cell.v.length > 0) {
                    const mergeInfo = worksheet['!merges']?.find(
                        (m) => R >= m.s.r && R <= m.e.r && C >= m.s.c && C <= m.e.c
                    );

                    let totalCellWidth = 0;
                    if (mergeInfo) {
                        for (let mc = mergeInfo.s.c; mc <= mergeInfo.e.c; mc++) {
                            totalCellWidth += colWidths[mc] || 15;
                        }
                    } else {
                        totalCellWidth = colWidths[C] || 15;
                    }

                    const explicitLines = cell.v.split('\n');
                    let totalCellLines = 0;

                    explicitLines.forEach((line) => {
                        totalCellLines += countWrappedLines(line, totalCellWidth);
                    });

                    const isRowSpan = mergeInfo && mergeInfo.e.r > mergeInfo.s.r;
                    if (!isRowSpan && totalCellLines > maxLinesInRow) {
                        maxLinesInRow = totalCellLines;
                    }
                }

                // 스타일 적용
                let typeStyle = {};
                let actualDomCell = null;

                if (domRow) {
                    const rowClass = domRow.className || '';
                    const cellClass = domCell ? (domCell.className || '') : '';

                    const isTypeTitle = rowClass.includes('table_title') || cellClass.includes('table_title');
                    const isTypeHeader = rowClass.includes('table_header') || cellClass.includes('table_header');
                    const isTypeCenter = rowClass.includes('colored') || cellClass.includes('colored');
                    const isTypeTime = rowClass.includes('search_time') || cellClass.includes('search_time');

                    if (isTypeTitle) {
                        typeStyle = titleStyle;
                    } else if (isTypeHeader && isTypeCenter) {
                        typeStyle = centerStyle;
                    } else if (isTypeHeader) {
                        typeStyle = headerStyle;
                    } else if (isTypeCenter) {
                        typeStyle = centerStyle;
                    } else if (isTypeTime) {
                        typeStyle = timeStyle;
                    };

                    if (rowClass.includes('bold') || cellClass.includes('bold')) {
                        typeStyle = {
                            ...typeStyle,
                            font: { ...(typeStyle.font || defaultBodyStyle.font), bold: true }
                        };
                    };

                    // 화면과 동일하게 데이터가 없는 셀(data-value=" ")은 회색(--st-odd)으로 구분
                    if (domCell?.dataset?.value === ' ') {
                        typeStyle = { ...typeStyle, fill: { fgColor: { rgb: 'F5F8FA' } } };
                    }
                }

                let appliedStyle = {
                    border: commonBorder,
                    ...defaultBodyStyle,
                    ...typeStyle,
                };

                // 타이틀/조회시각처럼 정렬이 명시된 스타일이 아니면 화면 정렬을 그대로 따름
                if (!typeStyle.alignment && domCell?.dataset?.excelAlign) {
                    appliedStyle.alignment = {
                        ...appliedStyle.alignment,
                        horizontal: domCell.dataset.excelAlign
                    };
                }

                appliedStyle.alignment = {
                    ...(appliedStyle.alignment || { horizontal: 'center', vertical: 'center' }),
                    wrapText: true,
                };

                // 빈 td 는 스텁 셀(t:'z')로 만들어지는데, 스텁은 스타일이 있어도 기록에서 통째로 누락되어 배경색이 사라짐.
                // 배경(fill)이 필요한 셀만 빈 문자열 셀로 변환해 스타일이 기록되게 함.
                if (cell.t === 'z' && appliedStyle.fill) {
                    cell.t = 's';
                    cell.v = '';
                }

                cell.s = appliedStyle;

                // 엑셀 개별 글자색 적용 (셀 클래스 기반)
                if (domCell) {
                    const cellClass = domCell.className || '';

                    if (cellClass.includes('excel_blue_text')) {
                        // ■ (파란색)
                        cell.s.font = { ...cell.s.font, color: { rgb: 'D4E9FF' }, bold: true };
                        cell.s.alignment = { horizontal: 'right', vertical: 'center' };
                    } else if (cellClass.includes('excel_red_text')) {
                        // * 참고 (빨간색)
                        cell.s.font = { ...cell.s.font, color: { rgb: 'FF0000' } };
                        cell.s.alignment = { horizontal: 'left', vertical: 'center' };
                    } else if (cellClass.includes('excel_black_text')) {
                        // 범례 글자 (검은색)
                        cell.s.font = { ...cell.s.font, color: { rgb: '000000' } };
                        cell.s.alignment = { horizontal: 'left', vertical: 'center' };
                    } else {
                        // 범례 이외의 일반 셀 중 * 들어간 셀만 빨간색 적용
                        const cellVal = String(cell.v || '');
                        if (cellVal.includes('*')) {
                            cell.s.font = { ...cell.s.font, color: { rgb: 'FF0000' } };
                        }
                    }
                }
            }

            // 명시적 \n + 자동 개행 수만큼 행 높이 산정
            const computedHeight = 22 + (maxLinesInRow - 1) * 16;
            rowHeights.push({ hpt: computedHeight });
        }

        worksheet['!rows'] = rowHeights;

        // 파일 저장
        const finalFileName = excelFileName.endsWith('.xlsx') ? excelFileName : `${excelFileName}.xlsx`;
        XLSX.writeFile(workbook, finalFileName);
    };

    return (
        <button
            disabled={disabled}
            onClick={excelDownload}
            className='excel_btn'
        >
            <Download16 className='co_white' />
            엑셀다운로드
        </button>
    );
};

export default FidelityExcelDownload;